import { computed, defineComponent, onMounted, PropType, ref } from "vue";
import SmeLayout from "../../layouts/SmeLayout";
import Utils from "../../utils/Utils";
import { SmeOrderJson } from "../../utils/SmeUtils";
import { OfferJson } from "../../OrderForm";
import InputText from "../../forms/InputText";
import Validator, { ValidationError } from "../../utils/Validator";
import Tracking from "../../utils/Tracking";
import InputCheckbox from "../../forms/InputCheckbox";
import { OfferTariff } from "../../utils/OfferUtils";

export default defineComponent({
    components: {
        SmeLayout,
        InputText,
        InputCheckbox,
    },

    template: `
        <SmeLayout>
            <h1 class="title-1">Sprawdź, czy dane z faktury są poprawne</h1>

            <div class="contentbox contentbox--white">
                <InputText label="Numer NIP" :modelValue="options.billing_nip" :disabled="true" />
                <InputText label="Numer PPE" :modelValue="options.ppe" :disabled="true" />
                <InputText label="Roczne zużycie" :modelValue="usageformat" @update:modelValue="form.usage = $event" unit="kWh" :edit="true" :disabled="!edits.usage" :required="true" :error="errors.usage" @edit="edits.usage = true" />
                <InputText v-if="visible_price" label="Cena za kWh netto" :modelValue="priceformat" @update:modelValue="form.price = $event" unit="zł" :edit="true" :disabled="!edits.price" :required="true" :error="errors.price" @edit="edits.price = true" />
                <InputText label="Numer telefonu" placeholder="Podaj numer telefonu" v-model="form.phone" :error="errors.phone" />

                <div class="StepSmeVerify__terms">
                    <InputCheckbox title="Wyrażam zgodę na otrzymywanie od Respect Energy S.A informacji dotyczących produktów i usług Respect Energy S.A za pośrednictwem telefonu." v-model="form.terms" :error="errors.terms" />
                </div>

                <div class="StepSmeVerify__buttons">
                    <button type="button" class="button button--primary button--full" @click="onAccept">Przejdź do oferty</button>

                    <p class="StepSmeVerify__document">
                        <b>Dane bazują na: </b>

                        <span v-for="doc in options.documents">
                            {{ doc.name }}
                        </span>
                    </p>
                </div>

                <hr>

                <p>Coś się nie zgadza?</p>

                <div class="StepSmeVerify__buttons2">
                    <button type="button" class="button button--secondary button--full" @click="onInvoiceClick">Wgraj inną fakturę</button>
                    <a href="/dla-firm/formularz-kontaktowy" class="button button--secondary button--full">Skontaktuj się z nami</a>
                </div>
            </div>
        </SmeLayout>
    `,

    emits: [
        'accept',
    ],

    props: {
        options: { type: Object as PropType<SmeOrderJson>, required: true },
    },

    setup(props, ctx) {
        const sent = ref(false);

        const edits = ref({
            usage: false,
            price: false,
        });

        let price = props.options.ocr_price?.toString()?.replace('.', ',');

        const form = ref({
            usage: props.options.usage?.toString(),
            price: price,
            phone: props.options.phone,
            terms: props.options.terms_trade_phone,
        });

        const errors = ref({
            usage: computed(() => inputError('usage', sent.value)),
            price: computed(() => inputError('price', sent.value)),
            phone: computed(() => inputError('phone', sent.value)),
            terms: computed(() => inputError('terms', sent.value)),
        });

        const visible_price = computed(() => {
            return props.options.tariff == OfferTariff.C11;
        });

        const edit = computed(() => {
            return edits.value.usage || edits.value.price;
        });

        const usageformat = computed(() => {
            return edits.value.usage ? form.value.usage : form.value.usage + ' kWh';
        });

        const priceformat = computed(() => {
            return edits.value.price ? form.value.price : form.value.price + ' zł';
        });

        function assertError(field: string, force: boolean = false) {
            if (field == 'usage' && (force || form.value.usage)) {
                Validator.assertNumber(form.value.usage);
            }

            if (visible_price.value && field == 'price' && (force || form.value.price)) {
                Validator.assertFloat(form.value.price);

                if (parseFloat(form.value.price?.replace(',', '.')) > 10) {
                    throw new ValidationError('Błędna wartość');
                }
            }

            if (field == 'phone' && form.value.phone) {
                Validator.assertPhone(form.value.phone);
            }

            if (field == 'terms' && force && form.value.phone && !form.value.terms) {
                throw new ValidationError('To pole jest wymagane');
            }
        }

        function assertValid() {
            assertError('usage', true);
            assertError('price', true);
            assertError('phone', true);
            assertError('terms', true);
        }

        function inputError(field: string, force: boolean = false) {
            try {
                assertError(field, force);
            } catch (e) {
                if (e instanceof ValidationError) {
                    return e.message;
                }

                return 'Błędny format pola';
            }
        }

        function setStep(step: string, offer?: OfferJson) {
            Utils.setStep(step, offer);
        }

        function onInvoiceClick() {
            Tracking.doEvent('b2bsme_invoicedata_invoiceupload');

            setStep('sme');
        }

        function onContactClick() {
            Tracking.doEvent('b2bsme_invoicedata_call');
        }

        async function onAccept() {
            sent.value = true;

            assertValid();

            const r = await Utils.setCart('verify-accept', {
                edited: edit.value,
                form: form.value,
            }, false);

            if (edit.value) {
                Tracking.doEvent('b2bsme_invoicedata_edit');
            } else {
                Tracking.doEvent('b2bsme_invoicedata_confirm');
            }

            ctx.emit('accept')
        }

        Utils.hideGreenTerms();

        Utils.attachBodyClass(['StepSmeVerify']);

        return {
            edits,
            form,
            errors,

            usageformat,
            priceformat,
            visible_price,

            setStep,
            onInvoiceClick,
            onContactClick,
            onAccept,
        };
    },
});
